import DiagnosticoSocioambientalService from '@/services/DiagnosticoSocioambientalService';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import {mapGetters} from 'vuex';
import {ProtocolosService} from '@/services/ProtocolosService';

const constProcessoFaseTerrasIndigenas = 'Processo da fase - Terras indígenas';
const constTerrasIndigenas = 'Terras indígenas';

export default {
    data() {
        return {
            camadas: [],
            carsConsultados: 0,
            cpfsCnpjsConsultados: 0,
            camadaCar: {}
        };
    },

    computed: {
        ...mapGetters(['usuarioLogado'])
    },

    methods: {
        mxIsItau() {
            const nameGroupUpperCaseUnaccented = this.usuarioLogado?.nameGroup
                ?.toUpperCase()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '');

            return nameGroupUpperCaseUnaccented === 'ITAU';
        },
        resetarValores() {
            this.carsConsultados = 0;
            this.cpfsCnpjsConsultados = 0;
            this.camadas = [];
        },
        mxOnLoadFileItau(file) {
            if (this.validateFileFormat(file)) {
                this.resetarValores();
                const reader = new FileReader();
                this.canExport = false;
                reader.onload = async e => {
                    let hasValidInfo = false;
                    const arrayBuffer = e.target.result;
                    const data = new Uint8Array(arrayBuffer);
                    const workbook = XLSX.read(data, { type: 'array' });

                    const sheetName = workbook.SheetNames[0];
                    const sheet = workbook.Sheets[sheetName];

                    const jsonData = XLSX.utils.sheet_to_json(sheet, {
                        header: 1, blankrows: false
                    });

                    const headerRow = jsonData[0];
                    const carCpfCnpjIndex = headerRow.findIndex(
                        column => column === 'CAR/CPF/CNPJ'
                    );

                    if (carCpfCnpjIndex === -1 || jsonData.length <= 1) {
                        this.showWarningMessage();
                        return;
                    }

                    const carCpfCnpjArray = jsonData.slice(1, 101).map(row => {
                        const value = row[carCpfCnpjIndex]
                            .toString()
                            .replace(/[^\w\s]/g, '');

                        if (
                            /^\d{11}$/.test(value) ||
                            /^\d{14}$/.test(value) ||
                            /^[A-Z]{2}\d{7}.+$/.test(value)
                        ) {
                            hasValidInfo = true;
                        }

                        return value;
                    });

                    if (!hasValidInfo) {
                        this.showWarningMessage();
                        return;
                    }

                    const { configuracaoProtocoloCamada } = (await ProtocolosService.buscarCamadasProtocolo(this.protocolo)).data;

                    const codigosAdicionados = new Set(
                        this.camadas.map(camada => camada.codigoCamada)
                    );
                    
                    for (const camada of configuracaoProtocoloCamada) {
                        if (!codigosAdicionados.has(camada.codigoCamada)) {
                            codigosAdicionados.add(camada.codigoCamada);
                            this.camadas.push({
                                ...camada,
                                carsConformes: 0,
                                carsInconformes: 0,
                                cpfsCnpjsConformes: 0,
                                cpfsCnpjsInconformes: 0
                            });
                        }
                    }

                    this.tableData = carCpfCnpjArray.map(item => ({
                        'CAR/Documento': item,
                        'Análise Socioambiental': this.$t(
                            'mensagens.diagnosticoSocioambiental.processando'
                        ),
                        'Área do CAR (ha)': '-'
                    }));

                    this.exportTable = carCpfCnpjArray.map(item => ({
                        'CAR/Documento': item,
                        camadas: [],
                        'Área do CAR (ha)': '-'
                    }));

                    for (let i = 0; i < carCpfCnpjArray.length; i++) {
                        this.canExport = false;
                        const item = carCpfCnpjArray[i];
                        let hasAlto = false;
                        let hasMedio = false;
                        let hasBaixo = false;

                        if (/^[A-Z]{2}\d{7}.+$/.test(item)) {
                            try {
                                let dataCAR = null;
                                let diagnosticos = null;
                                const response =
                                    await DiagnosticoSocioambientalService.consultar(
                                        'CODIGOCAR',
                                        this.formatCAR(item),
                                        true,
                                        false,
                                        null,
                                        this.protocolo
                                    );

                                if (response.data) {
                                    dataCAR = response.data.dataCAR[0];
                                    this.carsConsultados++;
                                    this.camadaCar = response?.data?.informacoes_gerais?.camadaCar ?? null;
                                    this.exportTable[i]['Situação'] =
                                        dataCAR.imovel.situacao;
                                    if (dataCAR.imovel.areaHa != null) {
                                        const ha = Number(dataCAR.imovel.areaHa).toFixed(1);
                                        this.tableData[i]['Área do CAR (ha)'] = ha;
                                        this.exportTable[i]['Área do CAR (ha)'] = ha;
                                    }
                                    if (dataCAR) {
                                        diagnosticos = dataCAR.diagnosticos;
                                    }
                                }

                                diagnosticos.forEach(diagnostico => {
                                    this.exportTable[i].camadas.push(
                                        diagnostico
                                    );
                                });

                                for (const diagnostico of diagnosticos) {
                                    const priority = diagnostico.nivelAlerta;

                                    if (priority === 'ALTO') {
                                        hasAlto = true;
                                    } else if (priority === 'MEDIO') {
                                        hasMedio = true;
                                    } else if (priority === 'BAIXO') {
                                        hasBaixo = true;
                                    }
                                }

                                const analysisResult =
                                    this.determineAnalysisResult(
                                        hasAlto,
                                        hasMedio,
                                        hasBaixo
                                    );
                                this.tableData[i]['Análise Socioambiental'] =
                                    analysisResult;
                            } catch (error) {
                                this.tableData[i]['Análise Socioambiental'] =
                                    this.$t(
                                        'mensagens.diagnosticoSocioambiental.naoEncontrado'
                                    );
                            }
                        } else if (
                            /^\d{11}$/.test(item) ||
                            /^\d{14}$/.test(item)
                        ) {
                            try {
                                let dataCAR = null;
                                const response =
                                    await DiagnosticoSocioambientalService.consultar(
                                        'CPF_CNPJ',
                                        item,
                                        true,
                                        false,
                                        null,
                                        this.protocolo
                                    );

                                if (response.data) {
                                    dataCAR = response.data.dataCAR;
                                    this.cpfsCnpjsConsultados++;
                                }

                                dataCAR.forEach(diagnostico => {
                                    this.exportTable[i].camadas.push(
                                        diagnostico
                                    );
                                });

                                for (const carItem of dataCAR) {
                                    const priority = carItem.nivelAlerta;

                                    if (priority === 'ALTO') {
                                        hasAlto = true;
                                    } else if (priority === 'MEDIO') {
                                        hasMedio = true;
                                    } else if (priority === 'BAIXO') {
                                        hasBaixo = true;
                                    }
                                }

                                const analysisResult =
                                    this.determineAnalysisResult(
                                        hasAlto,
                                        hasMedio,
                                        hasBaixo
                                    );
                                this.tableData[i]['Análise Socioambiental'] =
                                    analysisResult;
                            } catch (error) {
                                this.tableData[i]['Análise Socioambiental'] =
                                    this.$t(
                                        'mensagens.diagnosticoSocioambiental.naoEncontrado'
                                    );
                            }
                        } else {
                            this.tableData[i]['Análise Socioambiental'] =
                                this.$t(
                                    'mensagens.diagnosticoSocioambiental.naoEncontrado'
                                );
                        }
                    }
                    this.canExport = true;
                };

                reader.readAsArrayBuffer(file.raw);
            }
        },
        mxExportToExcelItau() {
            const filename = 'diagnostico-socioambiental-lote.xlsx';
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Sheet1');

            worksheet.addRows([
                ['Data da consulta:', new Date().toLocaleDateString('pt-BR')],
                ['CARs consultados:', this.carsConsultados],
                ['CPFs/CNPJs consultados:', this.cpfsCnpjsConsultados],
                [],
                [
                    'Camada',
                    'CARs',
                    'CPFs/CNPJs',
                    'Fonte',
                    'URL',
                    'Data atualização'
                ]
            ]);

            for (let i = 1; i <= 3; i++) {
                worksheet.getRow(i).getCell(1).font = { bold: true };
            }

            worksheet.getRow(5).eachCell(cell => {
                cell.font = { bold: true };
            });

            const rowsCamadas = [];
            const codigosAdicionados = new Set();

            this.camadas.forEach(camada => {
                if (!codigosAdicionados.has(camada.codigoCamada)) {
                    codigosAdicionados.add(camada.codigoCamada);
                    rowsCamadas.push(
                        worksheet.addRow([
                            camada.nomeCamada,
                            '',
                            '',
                            camada.nomeFonteCamada,
                            camada.urlFonteCamada,
                            camada.dataCadastroFonteCamada
                        ])
                    );
                }
            });

            if (this.carsConsultados > 0 && this.camadaCar) {
                worksheet.addRow([
                    'CAR',
                    '-',
                    '-',
                    this.camadaCar.fontes?.[0]?.nome ?? '-',
                    this.camadaCar.fontes?.[0]?.url ?? '-',
                    this.camadaCar.dataAtualizacaoDados ?? '-'
                ]);
            }

            worksheet.addRow();

            const nomesCamadas = this.camadas.map(camada => camada.nomeCamada);

            const indexCamadaTerrasIndigenas = nomesCamadas.indexOf(constTerrasIndigenas);
            if (indexCamadaTerrasIndigenas !== -1) {
                nomesCamadas.splice(indexCamadaTerrasIndigenas + 1, 0, constProcessoFaseTerrasIndigenas);
            }

            const cabecalhoAnalise = worksheet.addRow([
                'Item Analisado',
                'Status',
                'Área do CAR (ha)',
                'Resultado',
                ...nomesCamadas
            ]);

            cabecalhoAnalise.eachCell(cell => {
                cell.font = { bold: true };
            });

            for (const row of this.exportTable) {
                const formattedData = this.formatTableData(
                    row,
                    'CAR/Documento',
                    row['CAR/Documento']
                );

                const rowData = [
                    formattedData,
                    this.mxGetSituacao(row),
                    row['Área do CAR (ha)'] || '-',
                    this.mxDetermineMaxNivelAlertaItau(row.camadas),
                    ...this.mxExtractCamadaValuesItau(row, nomesCamadas)
                ];

                const newRow = worksheet.addRow(rowData);

                newRow.getCell(1).font = { bold: false };
            }

            rowsCamadas.forEach(row => {
                const celulaCamada = row.getCell(1);
                const camada = this.camadas.find(
                    camada => camada.nomeCamada === celulaCamada.value
                );

                let valorColunaCars;
                let valorColunaCpfsCnpjs;

                if (camada.carsConformes + camada.carsInconformes) {
                    if (camada.carsInconformes) {
                        valorColunaCars = 'Inconforme';
                    } else {
                        valorColunaCars = 'Conforme';
                    }
                } else {
                    valorColunaCars = '-';
                }

                if (camada.cpfsCnpjsConformes + camada.cpfsCnpjsInconformes) {
                    if (camada.cpfsCnpjsInconformes) {
                        valorColunaCpfsCnpjs = 'Inconforme';
                    } else {
                        valorColunaCpfsCnpjs = 'Conforme';
                    }
                } else {
                    valorColunaCpfsCnpjs = '-';
                }

                row.getCell(2).value = valorColunaCars;
                row.getCell(3).value = valorColunaCpfsCnpjs;
            });

            for (let i = 1; i <= 3; i++) {
                const coluna = worksheet.getColumn(i);
                const tamanhos = coluna.values.map(value =>
                    value ? value.toString().length : 0
                );
                const maiorTamanho = Math.max(
                    ...tamanhos.filter(v => typeof v === 'number')
                );
                coluna.width = maiorTamanho + 2;
            }

            for (let i = 4; i <= 5; i++) {
                const coluna = worksheet.getColumn(i);
                const tamanhos = coluna.values.map(value =>
                    value ? value.toString().length : 0
                );
                let indexInicio;
                let indexFim;

                for (let i = 0; i < tamanhos.length; i++) {
                    if (tamanhos[i] && !indexInicio) {
                        indexInicio = i;
                    }

                    if (indexInicio && !tamanhos[i]) {
                        indexFim = i;
                        break;
                    }
                }

                const maiorTamanho = Math.max(
                    ...tamanhos
                        .slice(indexInicio, indexFim)
                        .filter(v => typeof v === 'number')
                );

                coluna.width = maiorTamanho;
            }

            for (let i = 6; i <= worksheet.columns.length; i++) {
                const coluna = worksheet.getColumn(i);
                coluna.eachCell(cell => {
                    if (cell._row._number > 5 && cell.style?.font) {
                        coluna.width = cell.value.length + 2;
                    }
                });
            }

            workbook.xlsx.writeBuffer().then(buffer => {
                const blob = new Blob([buffer], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);
                link.download = filename;
                link.click();
                window.URL.revokeObjectURL(link.href);
            });
        },
        mxGetSituacao(row) {
            const situacao = row['Situação'];

            switch (situacao) {
                case 'AT':
                    return this.$t(
                        'mensagens.diagnosticoSocioambiental.situacoes.ativo'
                    );
                case 'PE':
                    return this.$t(
                        'mensagens.diagnosticoSocioambiental.situacoes.pendente'
                    );
                case 'SU':
                    return this.$t(
                        'mensagens.diagnosticoSocioambiental.situacoes.suspenso'
                    );
                case 'CA':
                    return this.$t(
                        'mensagens.diagnosticoSocioambiental.situacoes.cancelado'
                    );
                default:
                    return '-';
            }
        },
        mxDetermineMaxNivelAlertaItau(camadas) {
            const levelsPriority = ['ALTO', 'MEDIO', 'BAIXO'];

            for (const level of levelsPriority) {
                for (const camada of camadas) {
                    if (camada.nivelAlerta === level) {
                        return this.determineMaxAlert(level);
                    }
                }
            }
            return this.$t(
                'mensagens.diagnosticoSocioambiental.naoEncontradoResultadoIndisponivel'
            )?.trim();
        },
        mxGetAnaliseCamadaItau(camada, tipoDiagnostico) {
            const camadaLista = this.camadas.find(
                camadaExistente =>
                    camadaExistente.codigoCamada === camada.camada.codigo
            );

            if (camada.nivelAlerta === 'BAIXO') {
                if (tipoDiagnostico === 'CPF_CNPJ') {
                    camadaLista.cpfsCnpjsConformes++;
                } else {
                    camadaLista.carsConformes++;
                }

                return this.$t('mensagens.diagnosticoSocioambiental.conforme');
            } else if (camada.resultados.length) {
                const resultados = [];

                const campos = [
                    ['Nome/Razão Social', 'nome_pessoa'],
                    ['Documento', 'cpf_cnpj'],
                    ['Área total(ha)', 'areaTotalFeatureHa'],
                    [
                        'Percentual de sobreposição(ha)',
                        'percentualSobreposicaoGeometria'
                    ],
                    ['Área restrição(ha)', 'areaHa'],
                    ['Tipo', 'tipo'],
                    ['Código de sobreposição', 'codigoSobreposicao'],
                    ['Número do Auto de Infração', 'numero_auto_infracao'],
                    ['Número do Auto de Infração', 'num_auto_infracao'],
                    ['Número do Auto de Infração', 'numero_aut'],
                    ['Descrição Auto de Infração', 'descricao_auto_infracao'],
                    ['Descrição da Infração', 'descricao_infracao'],
                    ['Número do TAD', 'numero_tad'],
                    ['Número do TAD', 'num_tad'],
                    ['Data do TAD', 'data_tad'],
                    ['Descrição do TAD', 'descricao_tad'],
                    ['Descrição do TAD', 'descricao_da_apreensao_embargo'],
                    ['Proprietário', 'autuado'],
                    ['Área', 'area_total'],
                    ['Área', 'area'],
                    ['Unidade de conservação', 'nome_uc'],
                    ['CNUC', 'cnuc'],
                    ['CNUC', 'codigo_cnuc'],
                    ['Série', 'serie'],
                    ['UF', 'uf'],
                    ['UF', 'sigla_estado'],
                    ['Estado', 'estado'],
                    ['Municipio', 'municipio'],
                    ['Data detecção', 'data_deteccao'],
                    ['Data detecção', 'datadetec'],
                    ['Ano detecção', 'anodetec'],
                    ['Código do alerta', 'codealerta'],
                    ['Fonte', 'fonte'],
                    ['Nome', 'nome'],
                    ['Nome', 'identifica'],
                    ['Nome', 'nome_proje'],
                    ['Nome', 'nm_comunid'],
                    ['Esfera', 'esfera'],
                    ['Natureza', 'ds_naturez'],
                    ['Natureza', 'natureza'],
                    ['IPHAN', 'co_iphan'],
                    ['Logradouro', 'no_logrado'],
                    ['Logradouro', 'nu_logrado'],
                    ['Classificação', 'ds_classif'],
                    ['Data de detecção', 'view_date'],
                    ['Data de detecção', 'dt_cadastr'],
                    ['Data de detecção', 'acq_date'],
                    ['Código', 'codigo_ti'],
                    ['Código', 'codigo'],
                    ['Código', 'codigo_do'],
                    ['Fase', 'fase'],
                    ['Modalidade', 'modalidade'],
                    ['Etnia', 'etnia'],
                    ['Etnia', 'pueblo_etn'],
                    ['Unidade', 'unidade_administrativa'],
                    ['Unidade', 'unidade'],
                    ['Plano de Manejo', 'possui_plano_manejo'],
                    ['Data administrativa', 'data_administrativa_procedencia'],
                    ['Estabelecimento', 'estabelecimento'],
                    ['Ano ação fiscal', 'ano_acao_fiscal'],
                    [
                        'Data inclusão trabalhadores',
                        'data_inclusao_cadastro_trabalhadores'
                    ],
                    ['Empregador', 'empregador'],
                    ['Trabalhadores', 'trabalhadores_envolvidos'],
                    ['CNAE', 'cnae'],
                    ['Área Embargada (ha)', 'area_ha'],
                    ['Área Embargada (ha)', 'areaHa']
                ];

                for (const resultado of camada.resultados) {
                    const resultadosCamada = [];

                    for (const campo of campos) {
                        try {
                            if (resultado[campo[1]]) {
                                this.mxAdicionarCampoResultadosItau(
                                    resultadosCamada,
                                    campo[0],
                                    resultado[campo[1]]
                                );
                                camadaLista.carsInconformes++;
                            } else if (resultado.properties[campo[1]]) {
                                this.mxAdicionarCampoResultadosItau(
                                    resultadosCamada,
                                    campo[0],
                                    resultado.properties[campo[1]]
                                );
                                camadaLista.cpfsCnpjsInconformes++;
                            }
                        } catch (e) {
                            continue;
                        }
                    }

                    resultadosCamada.push(`Criticidade da Restrição: ${camada.nivelAlerta}`);

                    resultados.push(resultadosCamada.join(', '));
                }

                return resultados.join('\n\n');
            }
        },
        mxAdicionarCampoResultadosItau(resultados, nome, valor) {
            resultados.push(`${nome}: ${valor.replace(/(\r\n|\n|\r)/gm, '')}`);
        },
        mxExtractCamadaValuesItau(row, headers) {
            const camadasResultado = row.camadas;
            const tipoDiagnostico =
                row['CAR/Documento'].length > 14 ? 'CAR' : 'CPF_CNPJ';

            return headers.map(header => {
                if (header.toLowerCase() === constProcessoFaseTerrasIndigenas.toLowerCase() && tipoDiagnostico === 'CAR') {
                    const camada = camadasResultado.find(item =>
                        item.camada.nome.toLowerCase() === constTerrasIndigenas.toLowerCase());
                    const resultado = this.mxGetAnaliseCamadaItau(camada, tipoDiagnostico);
                    return this.mxIdentificarProcessoFaseTerrasIndigenas(resultado);
                }

                const camada = camadasResultado.find(
                    camada => camada.camada.nome === header
                );
                return camada
                    ? this.mxGetAnaliseCamadaItau(camada, tipoDiagnostico)
                    : '-';
            });
        },
        mxIdentificarProcessoFaseTerrasIndigenas(resultadoTerraIndigena) {
            const resultado = resultadoTerraIndigena.toLowerCase();

            if (resultado.includes('Fase: Regularizada'.toLowerCase())) {
                return 'Homologada';
            }

            if (resultado.includes('Fase: Homologada'.toLowerCase())) {
                return 'Homologada';
            }

            if (resultado.includes('Fase: Declarada'.toLowerCase())) {
                return 'Não homologada';
            }

            if (resultado.includes('Fase: Delimitada'.toLowerCase())) {
                return 'Não homologada';
            }

            if (resultado.includes('Fase: Encaminhada RI'.toLowerCase())) {
                return 'Não homologada';
            }

            if (resultado.includes('Fase: Em Estudo'.toLowerCase())) {
                return 'Não homologada';
            }

            return '-';
        }
    }
};
